/**
 * Componenet that display the details of a specific authorisation
 * @author Liwei Yeh <liwei.yeh@adasasistemas.com>
 */

// Library
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Tabs } from '@general';
// Assests
import { Droplet } from '@public';
// Types
import { AuthType } from '@types';
import { AuthorisationDisplayController } from '@utils';
import { isEmpty } from 'lodash';

// Styles
import styles from './authorisationDetails.module.scss';

export interface AuthorisationDetailsProp {
	auth: AuthType;
}

export const AuthorisationDetails = ({ auth }: AuthorisationDetailsProp) => {
	// Displayer
	const authDisplayer = new AuthorisationDisplayController(auth);
	const { labels: authLabels, visible: authVisible, values: authValues } = authDisplayer;
	// State
	const [CONDITIONS, DETAILS, LANDS] = ['Conditions', 'Details', authLabels.attachedLands];
	const [selectedTab, setSelectedTab] = useState(DETAILS);
	// Memo
	const hasConditionsTab = useMemo(() => {
		return authVisible.conditions || authVisible.flowCondition || authVisible.otherCondition;
	}, [authVisible.conditions, authVisible.flowCondition, authVisible.otherCondition]);
	const hasLandsTab = useMemo(
		() => authVisible.attachedLands && authValues.attachedLands.length >= 6,
		[authVisible.attachedLands, authValues.attachedLands],
	);
	const availableTabs: string[] = useMemo(() => {
		const tabs = [DETAILS];
		if (hasConditionsTab) {
			tabs.push(CONDITIONS);
		}
		if (hasLandsTab) {
			tabs.push(LANDS);
		}
		return tabs;
	}, [DETAILS, CONDITIONS, LANDS, hasConditionsTab, hasLandsTab]);

	// Helpers
	// Reset selected tab on changed authorisation
	useEffect(() => {
		setSelectedTab(DETAILS);
	}, [DETAILS, auth.id]);

	const detailsContent = [
		{
			header: authLabels.reference,
			content: authValues.reference,
			show: authVisible.reference,
		},
		{
			header: authLabels.expiryDate,
			content: authValues.expiryDate,
			show: authVisible.expiryDate,
		},
		{
			header: authLabels.waterPlan,
			content: authValues.waterPlan,
			show: authVisible.waterPlan,
		},
		{
			header: authLabels.managementGroupArea,
			content: authValues.managementGroupArea,
			show: authVisible.managementGroupArea,
		},
		{
			header: authLabels.managementSubgroup,
			content: authValues.managementSubgroup,
			show: authVisible.managementSubgroup,
		},
		{
			header: authLabels.location,
			content: authValues.location,
			show: authVisible.location,
		},
		{
			header: authLabels.place,
			content: authValues.place,
			show: authVisible.place,
		},
		{
			header: authLabels.source,
			content: authValues.source,
			show: authVisible.source,
		},
		{
			header: authLabels.sourceLocation,
			content: authValues.sourceLocation,
			show: authVisible.sourceLocation,
		},
		{
			header: authLabels.additionalDescriptions,
			content: authValues.additionalDescriptions,
			show: authVisible.additionalDescriptions,
		},
		{
			header: authLabels.attachedLands,
			content: hasLandsTab ? `Please select the '${LANDS}' tab to view` : authValues.attachedLands,
			show: authVisible.attachedLands,
			isSpecial: hasLandsTab,
		},
		{
			header: authLabels.nominalEntitlement,
			content: authValues.nominalEntitlement,
			show: authVisible.nominalEntitlement,
		},
		{
			header: authLabels.nominalVolume,
			content: authValues.nominalVolume,
			show: authVisible.nominalVolume,
		},
		{
			header: authLabels.maxVolume,
			content: authValues.maxVolume,
			show: authVisible.maxVolume,
		},
		{
			header: authLabels.maxAreaHa,
			content: authValues.maxAreaHa,
			show: authVisible.maxAreaHa,
		},
		{
			header: authLabels.volumetricLimits,
			content: authValues.volumetricLimits,
			show: authVisible.volumetricLimits,
		},
		{
			header: authLabels.maximumRates,
			content: authValues.maximumRates,
			show: authVisible.maximumRates,
		},
		{
			header: authLabels.purpose,
			content: authValues.purpose,
			show: authVisible.purpose,
		},
		{
			header: authLabels.capacity,
			content: authValues.capacity,
			show: authVisible.capacity,
		},
		{
			header: authLabels.priority,
			content: authValues.priority,
			show: authVisible.priority,
		},
		{
			header: authLabels.waterAllocationGroup,
			content: authValues.waterAllocationGroup,
			show: authVisible.waterAllocationGroup,
		},
		{
			header: authLabels.office,
			content: authValues.office,
			show: authVisible.office,
		},
	];

	const conditionsContent = [
		{
			header: authLabels.conditions,
			content: authValues.conditions,
			show: authVisible.conditions,
		},
		{
			header: authLabels.flowCondition,
			content: authValues.flowCondition,
			show: authVisible.flowCondition,
		},
		{
			header: authLabels.otherCondition,
			content: authValues.otherCondition,
			show: authVisible.otherCondition,
		},
	];

	const landsContent = [
		{
			header: authLabels.attachedLands,
			content: authValues.attachedLands,
			show: authVisible.attachedLands && hasLandsTab,
		},
	];

	const SwitchSelectedTabSection = () => {
		switch (selectedTab) {
			case DETAILS:
				return createSection(detailsContent);
			case CONDITIONS:
				return createSection(conditionsContent);
			case LANDS:
				return createSection(landsContent);
		}
	};

	const createSection = (
		sectionContent: Array<{
			header: string;
			content: string | string[] | null | undefined;
			show: boolean;
			isSpecial?: boolean;
		}>,
	) => {
		return (
			<div>
				{sectionContent.map(({ header, content, show, isSpecial: stylize }, id) => {
					return (
						<Fragment key={`section-${id}`}>
							{show && (
								<>
									{Array.isArray(content) ? (
										<div className={[styles.detail, styles['detail-vertical']].join(' ')}>
											<p className={styles['detail-header']}>{header}</p>
											{isEmpty(content) ? (
												<p>N/A</p>
											) : (
												<ul className={styles[`detail-list`]}>
													{content.map((item, key) => (
														<li className={styles[`detail-list-item`]} key={`content-${key}`}>
															<p>{item}</p>
														</li>
													))}
												</ul>
											)}
										</div>
									) : stylize ? (
										<div className={[styles.detail, styles['detail-vertical']].join(' ')}>
											<p className={styles['detail-header']}>{header}</p>
											<p className={styles['detail-special']}>{content}</p>
										</div>
									) : (
										<div className={styles.detail}>
											<p className={styles['detail-header']}>{header}</p>
											<p className={styles['detail-content']}>{content}</p>
										</div>
									)}
								</>
							)}
						</Fragment>
					);
				})}
			</div>
		);
	};

	return (
		<div className={styles.container} data-testid="authorisationDetails">
			<div className={styles.title}>
				<h2> Authorisation details</h2>
				<Droplet customStyles={styles['title-icon']} />
			</div>
			<div className={styles['border-title']} />
			<div className={styles.brief}>
				<h3>{authValues.managementSubgroup}</h3>
				<p className={styles.ref}>
					{authLabels.type}: {authValues.type}
				</p>
			</div>
			<div className={styles.tabs}>
				<Tabs
					tabs={availableTabs}
					handleClick={name => setSelectedTab(name)}
					selectedTab={selectedTab}
				/>
				<div className={styles['border-tab']} />
			</div>
			<div className={styles[`sublist-container`]}>{SwitchSelectedTabSection()}</div>
		</div>
	);
};
